import { useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useToast } from '../../contexts/ToastContext';
import { useAuthOptional } from '../../contexts/AuthContext';
import { normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import { subscribeToMasterAlerts } from '../../utils/email-alerts/subscribe-to-master-alerts';
import { trackEvent } from '../../utils/analytics/analytics';
import { shouldShowPostSignupNudge, markPostSignupNudgeShown } from '../../utils/engagement/post-signup-nudge';
import { useFormatMessage } from '../../i18n-review/client/Msg';

const messages = defineMessages({
  offer: {
    id: 'postSignup.alertNudge.offer',
    defaultMessage: 'Welcome to JobCrawls! Want an email when new jobs matching your interests appear?',
    description: 'One-time toast offering to turn on job alerts right after a first-ever sign-up',
  },
  turnOn: {
    id: 'postSignup.alertNudge.turnOn',
    defaultMessage: 'Turn on',
    description: 'Toast action that enables job alerts from the post-signup nudge',
  },
  enabled: {
    id: 'postSignup.alertNudge.enabled',
    defaultMessage: "You're set. We'll email you about new jobs matching your searches.",
    description: 'Toast confirming job alerts were enabled from the post-signup nudge',
  },
  pendingConfirmation: {
    id: 'postSignup.alertNudge.pendingConfirmation',
    defaultMessage: 'Almost there: confirm your email in your inbox to activate alerts.',
    description: 'Toast when enabling job alerts still needs the account email confirmed',
  },
  failed: {
    id: 'postSignup.alertNudge.failed',
    defaultMessage: "Couldn't turn on job alerts. You can enable them any time from the Alerts page.",
    description: 'Toast when enabling job alerts from the post-signup nudge fails',
  },
});

/**
 * TEC-514 #5: the "riskiest item in scope" per the ticket, a light, non-blocking value-prop
 * moment after first-ever signup, deliberately NOT a blocking interstitial or a new route (that
 * tradeoff, added friction on the signup redirect itself, is exactly what the ticket flags as
 * needing separate evaluation before shipping). This is a single dismissible toast, mounted
 * wherever ToastProvider already lives, that fires at most once ever per browser.
 *
 * Renders nothing itself: side-effect-only, same shape as a "fire an event on mount" component.
 * Known scope boundary: mounted in the three provider trees covering this ticket's own funnel,
 * LayoutDefault's main tree, its job-detail island (#job-ssr, where JobDetailAlertCta's
 * continue-as-guest flow lives), and SrpApp's SRP island (where SrpAlertCta's does), not the
 * salary/locations/best-companies-dimension content-hub islands (layouts/LayoutDefault.tsx),
 * where a first-ever-login landing is a much rarer path unrelated to this ticket's own CTAs.
 */
export function PostSignupAlertNudge(): null {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { showToast } = useToast();
  const auth = useAuthOptional();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!auth?.isAuthenticated) return;
    if (!shouldShowPostSignupNudge()) return;
    firedRef.current = true;
    markPostSignupNudgeShown();

    trackEvent({ name: 'post_signup_alert_nudge', params: { action: 'shown', locale: intl.locale } });

    const handleEnableAlerts = async () => {
      const locale = normalizeSupportedLocale(intl.locale);
      trackEvent({ name: 'post_signup_alert_nudge', params: { action: 'accepted', locale: intl.locale } });
      try {
        await subscribeToMasterAlerts(locale);
        let status: string | undefined;
        try {
          const res = await fetch('/api/alerts/subscription');
          if (res.ok) status = (await res.json())?.subscription?.status;
        } catch {
          // Status check is cosmetic; fall through to the generic confirmation.
        }
        showToast(
          formatMessage(status === 'pendingConfirmation' ? messages.pendingConfirmation : messages.enabled),
          'success',
          5000
        );
      } catch {
        showToast(formatMessage(messages.failed), 'error', 5000);
      }
    };

    showToast(formatMessage(messages.offer), 'success', 10000, {
      label: formatMessage(messages.turnOn),
      onClick: () => void handleEnableAlerts(),
    });
    // Fires at most once per mount (firedRef), independent of these values changing later:
    // re-running on a locale switch mid-toast would just re-fire the same one-shot nudge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.isAuthenticated]);

  return null;
}
